import { StrictMode, type ComponentType, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { FluentProvider } from '@fluentui/react-components';
// Self-hosted, so the type is the same on every machine (the mockups vendor the same faces).
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { useSystemTheme } from './lib/useSystemTheme';

type Auth = ComponentType<{ children: ReactNode }>;

function Root({ Auth }: { Auth: Auth }) {
  const theme = useSystemTheme();
  return (
    <FluentProvider theme={theme}>
      <BrowserRouter>
        <Auth>
          <App />
        </Auth>
      </BrowserRouter>
    </FluentProvider>
  );
}

// Fixture mode (`vite build --mode fixtures`, .env.fixtures) swaps IAM + vpn-api for the
// in-memory sample estate. The flag is a build-time constant, so a production build drops the
// branch and never emits the fixtures chunk.
async function authProvider(): Promise<Auth> {
  if (import.meta.env.VITE_FIXTURES === '1') {
    return (await import('./fixtures/FixtureAuthProvider')).FixtureAuthProvider;
  }
  return AuthProvider;
}

void authProvider().then((Auth) =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Root Auth={Auth} />
    </StrictMode>,
  ),
);
